// The exported code uses Tailwind CSS. Install Tailwind CSS in your dev environment to ensure all styles work.

import React, { useState, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { OutletContextType } from "../../types/GlobalType";
import Header from "../../component/_elements/Header.tsx";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  personalAttendanceController,
  PonctualitePersonalType,
  PonctualiteType,
  studentAttendanceController,
} from "../../_api/controllers/attendance.controller.ts";
import {
  classeController,
  promotionController,
  schoolYearController,
} from "../../_api/controllers/scolarite.controller.ts";
import {
  studentControler,
  StudentType,
} from "../../_api/controllers/student.controller.ts";
import moment from "moment";
import { capitalizeFirstLetter } from "../../_services/utils.jsx";
import toast from "react-hot-toast";
import {
  personalController,
  PersonalType,
} from "../../_api/controllers/personal.controller.ts";
import { autoTable } from "jspdf-autotable";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";

// Coordonnées affichées dans l'en-tête des exports (PDF/Excel).
// TODO: remplacer le numéro de téléphone par le vrai contact de l'établissement.
const SCHOOL_INFO = {
  name: "ETPM",
  fullName: "Ecole Technique Professionnelle Des Metiers",
  telephone: "+243810458708",
};

//skeleton component for attendance page loading state
const AttendanceSkeleton: React.FC<{ isDarkMode: boolean }> = ({
  isDarkMode,
}) => (
  <main className="ml-16 sm:ml-20 md:ml-0 p-6 mt-16 animate-pulse">
    {/* Header */}
    <div className="mb-6 flex justify-between items-center">
      <div>
        <div
          className={`h-6 w-64 rounded mb-2 ${
            isDarkMode ? "bg-gray-700" : "bg-gray-300"
          }`}
        />
        <div
          className={`h-4 w-96 rounded ${
            isDarkMode ? "bg-gray-600" : "bg-gray-200"
          }`}
        />
      </div>
    </div>

    {/* Statistics */}
    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
      {[...Array(4)].map((_, i) => (
        <div
          key={i}
          className={`rounded-lg border p-4 flex flex-col items-center ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div
            className={`w-12 h-12 rounded-full mb-3 ${
              isDarkMode ? "bg-gray-700" : "bg-gray-300"
            }`}
          />
          <div
            className={`h-3 w-20 mb-2 rounded ${
              isDarkMode ? "bg-gray-600" : "bg-gray-200"
            }`}
          />
          <div
            className={`h-6 w-20 rounded ${
              isDarkMode ? "bg-gray-700" : "bg-gray-300"
            }`}
          />
        </div>
      ))}
    </div>

    {/* Table container */}
    <div
      className={`rounded-lg border overflow-hidden mb-6 ${
        isDarkMode ? "border-gray-700" : "border-gray-200"
      }`}
    >
      {/* Table header */}
      <div
        className={`px-4 py-3 border-b ${
          isDarkMode
            ? "bg-[#161B22] border-gray-700"
            : "bg-gray-50 border-gray-200"
        }`}
      >
        <div
          className={`h-4 w-64 rounded ${
            isDarkMode ? "bg-gray-700" : "bg-gray-300"
          }`}
        />
      </div>

      {/* Table skeleton */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className={isDarkMode ? "bg-[#161B22]" : "bg-gray-50"}>
            <tr>
              {[...Array(6)].map((_, i) => (
                <th key={i} className="px-6 py-4">
                  <div
                    className={`h-4 rounded ${
                      isDarkMode ? "bg-gray-700" : "bg-gray-300"
                    }`}
                  />
                </th>
              ))}
            </tr>
          </thead>

          <tbody
            className={`${isDarkMode ? "bg-[#0D1117]" : "bg-white"} divide-y ${
              isDarkMode ? "divide-gray-700" : "divide-gray-200"
            }`}
          >
            {[...Array(6)].map((_, row) => (
              <tr key={row}>
                {[...Array(6)].map((_, col) => (
                  <td key={col} className="px-6 py-4">
                    <div
                      className={`h-4 rounded ${
                        isDarkMode ? "bg-gray-700" : "bg-gray-200"
                      }`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </main>
);

const Attendance: React.FC = () => {
  const { isDarkMode, setIsDarkMode } = useOutletContext<OutletContextType>();
  const [currentYear, setCurrentYear] = useState("2025 - 2026");
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const toggleYearDropdown = () => {
    setIsYearDropdownOpen(!isYearDropdownOpen);
  };
  // États pour la gestion des présences
  const [selectedDate, setSelectedDate] = useState<string>(
    moment().format("YYYY-MM-DD"),
  );

  // console.log(selectedDate);

  const [selectedClass, setSelectedClass] =
    useState<string>("Toutes les classes");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<any>("tous");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(30);
  const [isJustifyModalOpen, setIsJustifyModalOpen] = useState<boolean>(false);
  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState<boolean>(false);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<string>("");

  const [justificationForm, setJustificationForm] = useState<{
    currentAttendanceCoteId: any;
    currentAttendanceId: any;
    reason: any;
  }>({
    currentAttendanceCoteId: null,
    currentAttendanceId: null,
    reason: null,
  });
  const [activeTab, setActiveTab] = useState<string>("students");
  const [selectedTeacher, setSelectedTeacher] = useState<any>(null);

  // console.log(selectedTeacher);

  const printRef = useRef<HTMLDivElement>(null);

  //fetch eleves
  const { data: eleveData, isLoading: isEleveLoading } = useQuery({
    queryKey: ["students"],
    queryFn: studentControler.getAll,
  });

  //fetch attendance of the current month
  // const date = moment().format("YYYY-MM-DD");
  const { data: attendanceData, isLoading: isAttendanceLoading } = useQuery({
    queryKey: ["studentAttendance"],
    // queryFn: () => studentAttendanceController.getAllByMonth(date), // fetch attendance of the current month
    queryFn: studentAttendanceController.getAll, // fetch all attendance records
  });

  // console.log(attendanceData);

  //fetch attendance of personal
  const {
    data: attendanceData_forPersonal,
    isLoading: isPersonalAttendanceLoading,
  } = useQuery({
    queryKey: ["personalAttendance"],
    queryFn: () => personalAttendanceController.getAll(),
  });

  //fetch promotion
  const { data: promotionsData, isLoading: isPromotionsLoading } = useQuery({
    queryKey: ["promotions"],
    queryFn: promotionController.getAll,
  });

  //fetch classe
  const { data: dataClasseObj, isLoading: isClassesLoading } = useQuery({
    queryKey: ["classes"],
    queryFn: classeController.getAll,
  });

  //fetch anne scolaire
  const { data: schoolYear, isLoading: isSchoolYearLoading } = useQuery({
    queryKey: ["schoolYears"],
    queryFn: schoolYearController.getAll,
  });

  const schoolYearArr = schoolYear?.data || [];
  const selectedSchoolYear = schoolYearArr.find((sy: any) => sy.selected);

  //fetch all Personnels
  const { data: personalsData, isLoading: isPersonalsLoading } = useQuery({
    queryKey: ["personals"],
    queryFn: personalController.getAll,
  });

  const dataClasses = dataClasseObj?.data ? dataClasseObj.data : [];
  const dataPromotionArr = promotionsData?.data ? promotionsData.data : [];
  const attendanceDataArr_forStudent = attendanceData?.data
    ? attendanceData.data
    : [];

  const attendanceDataArr_forPersonal = attendanceData_forPersonal?.data || [];

  const incompleteSudentsArr = eleveData?.data ? eleveData.data : [];
  const incompletePersonalArr = personalsData?.data ? personalsData.data : [];

  // console.log(attendanceData);
  // console.log(attendanceDataArr_forStudent);
  // console.log(attendanceData_forPersonal);
  // console.log(attendanceData_forPersonal?.data);
  // console.log(attendanceDataArr_forPersonal);
  // console.log(incompletePersonalArr);

  // Selected data by date for Students and for Personal
  const attendancesDataByDate_forStudent: PonctualiteType[] =
    attendanceDataArr_forStudent?.filter(
      (attendance: PonctualiteType) =>
        moment(attendance.created_at).format("YYYY-MM-DD") === selectedDate,
    );

  const attendancesDataByDate_forPersonal: PonctualitePersonalType[] =
    attendanceDataArr_forPersonal.filter(
      (attendance) =>
        moment(attendance.created_at).format("YYYY-MM-DD") === selectedDate,
    );

  //Create explicite dataArray for students and for personal
  let completeStudents: StudentType[] = incompleteSudentsArr.map((student) => {
    let parentData: any,
      promotionData: any,
      classeDesignation: any,
      classeId: any,
      coteJustification: any = null,
      coteHeureArrivee: any = null,
      coteEleveId: any = 3,
      coteId: any = null,
      ponctualiteId: any = null,
      coteCreatedAt: any = null;

    for (const promotion of dataPromotionArr) {
      if (
        promotion.annee_scolaire_id === selectedSchoolYear?.id &&
        promotion.eleve_id === student.eleve_id
      ) {
        promotionData = promotion;
        //it is possible to get all promotion of the current student here by handling the condition above
      }
    }
    for (const classe of dataClasses) {
      if (promotionData?.classe_id === classe.id) {
        classeDesignation = classe.designation;
        classeId = classe.id;
      }
    }

    for (const element of attendancesDataByDate_forStudent) {
      if (student.eleve_id === element.eleve_id) {
        coteEleveId = element.cote_eleve_id;
        coteId = element.id;
        ponctualiteId = element.id;
        coteJustification = element.justification;
        coteHeureArrivee = element.heure_arrivee;
        coteCreatedAt = element.created_at;
      }
    }

    return {
      ...student,
      parentData: parentData,
      promotionData: promotionData,
      classeDesignation: classeDesignation,
      classeId: classeId,
      coteId: coteId,
      coteEleveId: coteEleveId,
      coteJustification: coteJustification,
      coteHeureArrivee: coteHeureArrivee,
      coteCreatedAt,
      ponctualiteId: ponctualiteId,
    };
  });

  let completePersonals: PersonalType[] = incompletePersonalArr.map(
    (personal) => {
      let coteJustification: any = null,
        coteHeureArrivee: any = null,
        cotePersonalId: any = 3,
        coteId: any = null,
        coteCreatedAt: any = null,
        ponctualiteId: any = null;

      for (const element of attendancesDataByDate_forPersonal) {
        if (personal.personnel_id === element.personnel_id) {
          cotePersonalId = element.cote_enseignant_id;
          coteId = element.id;
          coteJustification = element.justification;
          coteHeureArrivee = element.heure_arrivee;
          coteCreatedAt = element.created_at;
          ponctualiteId = element.id;
        }
      }

      return {
        ...personal,
        coteId: coteId,
        cotePersonalId: cotePersonalId,
        coteJustification: coteJustification,
        coteHeureArrivee: coteHeureArrivee,
        coteCreatedAt,
        ponctualiteId: ponctualiteId,
      };
    },
  );

  // console.log(completeStudents);
  // console.log(completePersonals);

  //filter out students without promotion in the selected school year
  completeStudents = completeStudents.filter(
    (student) => student.promotionData,
  );
  //filter out personals who are active
  completePersonals = completePersonals.filter((personal) => personal.status);

  const students: any = [...completeStudents];
  const teachers: any = [...completePersonals];

  // Fonction pour filtrer les élèves
  const filteredStudents = students.filter((student: any) => {
    const matchesSearch =
      student?.nom.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.prenom?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.postnom?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesClass =
      selectedClass === "Toutes les classes" ||
      student.classeDesignation?.toLowerCase() === selectedClass.toLowerCase();

    const matchesStatus =
      statusFilter === "tous" || student.coteEleveId === statusFilter;

    return matchesSearch && matchesClass && matchesStatus;
  });

  // console.log(teachers);
  // Fonction pour filtrer les personnels
  const filteredTeachers = teachers.filter(
    (
      teacher: PersonalType & {
        cotePersonalId: number;
        coteJustification: string;
        coteId: number;
      },
    ) => {
      // Filtre par recherche
      const matchesSearch =
        teacher?.nom.toLowerCase().includes(searchQuery.toLowerCase()) ||
        teacher?.prenom.toLowerCase().includes(searchQuery.toLowerCase()) ||
        teacher?.postnom.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus =
        statusFilter === "tous" || teacher.cotePersonalId === statusFilter;
      // if (
      //   searchQuery &&
      //   !teacher.nom.toLowerCase().includes(searchQuery.toLowerCase())
      // )
      //   return false;
      // Filtre par statut
      // if (statusFilter !== "tous" && teacher.cotePersonalId !== statusFilter)
      //   return false;
      // return true;

      return matchesSearch && matchesStatus;
    },
  );

  // Calcul pour la pagination des élèves
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedStudents = filteredStudents.slice(startIndex, endIndex);

  // Calcul pour la pagination des enseignants
  const totalTeacherPages = Math.ceil(filteredTeachers.length / itemsPerPage);
  const teacherStartIndex = (currentPage - 1) * itemsPerPage;
  const teacherEndIndex = teacherStartIndex + itemsPerPage;
  const paginatedTeachers = filteredTeachers.slice(
    teacherStartIndex,
    teacherEndIndex,
  );
  // console.log(students.length);
  // Calcul des statistiques pour les élèves
  const totalStudents =
    selectedClass === "Toutes les classes"
      ? students.length
      : students.filter((s: any) => s.classeDesignation === selectedClass)
          .length;

  const presentStudents =
    selectedClass === "Toutes les classes"
      ? students.filter((s: any) => s.coteEleveId === 1).length //present pour ttes les classes
      : students.filter(
          (s: any) =>
            s.classeDesignation === selectedClass && s.coteEleveId === 1, //presence pour une classe X
        ).length;

  const absentStudents =
    selectedClass === "Toutes les classes"
      ? students.filter((s: any) => s.coteEleveId === 3).length // absent pour ttes les classes
      : students.filter((s: any) => {
          return s.classeDesignation === selectedClass && s.coteEleveId === 3;
        }).length;

  const lateStudents =
    selectedClass === "Toutes les classes"
      ? students.filter((s: any) => s.coteEleveId === 2).length
      : students.filter(
          (s: any) =>
            s.classeDesignation === selectedClass && s.coteEleveId === 2,
        ).length;

  const attendanceRate =
    totalStudents > 0
      ? (((presentStudents + lateStudents) / totalStudents) * 100).toFixed(1)
      : 0;

  // Calcul des statistiques pour les enseignants
  const totalTeachers = teachers.length;
  const presentTeachers = teachers.filter(
    (t: PersonalType & { cotePersonalId: number }) => t.cotePersonalId === 1,
  ).length;
  const lateTeachers = teachers.filter(
    (t: PersonalType & { cotePersonalId: number }) => t.cotePersonalId === 2,
  ).length;
  const absentTeachers = teachers.filter(
    (t: PersonalType & { cotePersonalId: number }) => t.cotePersonalId === 3,
  ).length;
  const teacherAttendanceRate =
    totalTeachers > 0
      ? (((presentTeachers + lateTeachers) / totalTeachers) * 100).toFixed(1)
      : 0;

  // Fonction pour ouvrir le modal de justification
  const openJustifyModal = (student: any) => {
    setSelectedStudent(student);
    setIsJustifyModalOpen(true);
  };

  // Fonction pour ouvrir le modal de justification pour enseignant
  const openJustifyTeacherModal = (teacher: any) => {
    setSelectedTeacher(teacher);
    setIsJustifyModalOpen(true);
  };

  // Fonction pour ouvrir le modal de notification
  // const openNotifyModal = (student: any) => {
  //   setSelectedStudent(student);
  //   setIsNotifyModalOpen(true);
  // };

  //Update attendance
  const queryClient = useQueryClient();

  const mutationUpdateAttendanceStudent = useMutation({
    mutationFn: () =>
      studentAttendanceController.update(selectedStudent.ponctualiteId, {
        justification: justificationForm.reason,
        cote_eleve_id: selectedStudent.coteEleveId,
      }),
    onSuccess: (data) => {
      setShowNotification(true);
      setNotificationMessage("Justification enregistrée avec succès");
      setTimeout(() => {
        setShowNotification(false);
        setNotificationMessage("");
      }, 3000);

      setIsJustifyModalOpen(false);
      setJustificationForm({
        reason: null,
        currentAttendanceId: null,
        currentAttendanceCoteId: null,
      });

      setSelectedStudent(null);
      setSelectedTeacher(null);

      setSelectedStudent(null);
      queryClient.invalidateQueries({ queryKey: ["studentAttendance"] });
    },

    onError: (err) => {
      toast.error("Une erreur est survenue, réessayer.", {
        duration: 4000,
        position: "bottom-right",
        style: {
          background: "red",
          color: "#fff",
          padding: "10px",
          fontSize: "14px",
          borderRadius: "5px",
        },
      });
    },
  });

  const mutationUpadateAttenadaceTeacher = useMutation({
    mutationFn: () =>
      personalAttendanceController.update(selectedTeacher.ponctualiteId, {
        justification: justificationForm.reason,
        cote_enseignant_id: selectedTeacher.cotePersonalId,
      }),
    onSuccess: (data) => {
      // console.log(data);
      setShowNotification(true);
      setNotificationMessage("Justification enregistrée avec succès");
      setTimeout(() => {
        setShowNotification(false);
        setNotificationMessage("");
      }, 3000);

      setIsJustifyModalOpen(false);
      setJustificationForm({
        reason: null,
        currentAttendanceId: null,
        currentAttendanceCoteId: null,
      });

      setSelectedStudent(null);
      setSelectedTeacher(null);

      setSelectedStudent(null);
      queryClient.invalidateQueries({ queryKey: ["personalAttendance"] });
    },

    onError: (err) => {
      // console.log(err);
      toast.error("Une erreur est survenue, réessayer.", {
        duration: 4000,
        position: "bottom-right",
        style: {
          background: "red",
          color: "#fff",
          padding: "10px",
          fontSize: "14px",
          borderRadius: "5px",
        },
      });
    },
  });

  const handleUpdateAttendance = () => {
    selectedStudent
      ? mutationUpdateAttendanceStudent.mutate()
      : mutationUpadateAttenadaceTeacher.mutate();
  };

  // Fonction pour envoyer une notification
  const handleNotifySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Dans une application réelle, cela enverrait une notification
    setIsNotifyModalOpen(false);
    // Afficher une notification
    setNotificationMessage("Notification envoyée aux parents avec succès");
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  // Fonction pour basculer le mode sombre
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  const dropdownItemClass = `flex items-center w-full px-4 py-2.5 text-sm text-left ${
    isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
  } !rounded-button whitespace-nowrap`;

  const dropdownPanelClass = `absolute top-full right-0 mt-1.5 w-48 rounded-lg shadow-lg overflow-hidden ${
    isDarkMode ? "bg-[#161B22]" : "bg-white"
  } z-10 border ${isDarkMode ? "border-gray-700" : "border-gray-200"}`;

  const toggleExportDropdown = () => {
    setIsExportDropdownOpen(!isExportDropdownOpen);
  };

  const ExportDropdownRef = React.useRef<HTMLDivElement>(null);

  // Fermeture des menus lors d'un clic à l'extérieur
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      //export button
      if (
        ExportDropdownRef.current &&
        !ExportDropdownRef.current.contains(event.target as Node)
      ) {
        setIsExportDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Affiche une notification éphémère en bas à droite de l'écran
  const showExportNotification = (
    message: string,
    type: "success" | "erreur",
  ) => {
    const el = document.createElement("div");
    el.className = `fixed bottom-4 right-4 px-6 py-3 rounded-lg text-white ${
      type === "success"
        ? isDarkMode
          ? "bg-green-600"
          : "bg-green-500"
        : isDarkMode
          ? "bg-red-600"
          : "bg-red-500"
    } shadow-lg transition-opacity duration-500`;
    el.innerHTML = `<i class="fas ${
      type === "success" ? "fa-check-circle" : "fa-circle-exclamation"
    } mr-2"></i>${message}`;
    document.body.appendChild(el);
    setTimeout(() => {
      el.style.opacity = "0";
      setTimeout(() => el.remove(), 500);
    }, 3000);
  };

  const anneeLabel = `Année scolaire ${selectedSchoolYear?.designation || currentYear}`;

  // Titre du document, basé sur le filtre classe + l'année scolaire actives
  const getExportTitle = () => {
    const classeLabel = selectedClass;
    return `Liste de ponctualité — ${classeLabel} — Le ${moment(selectedDate).format("DD/MM/YYYY")}`;
  };

  // Dessine l'en-tête professionnel commun (logo textuel + coordonnées) sur le PDF
  const drawPdfHeader = (doc: jsPDF) => {
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(37, 99, 235);
    doc.text(SCHOOL_INFO.name, 14, 18);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(SCHOOL_INFO.fullName, 14, 24);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(anneeLabel, 14, 28);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.text(`Tél: ${SCHOOL_INFO.telephone}`, pageWidth - 14, 18, {
      align: "right",
    });

    doc.setDrawColor(210, 210, 210);
    doc.line(14, 30, pageWidth - 14, 28);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(20, 20, 20);
    doc.text(getExportTitle(), pageWidth / 2, 38, { align: "center" });

    return 44;
  };

  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const startY = drawPdfHeader(doc);
    console.log(paginatedStudents);
    const rows = paginatedStudents
      .sort((a: any, b: any) =>
        a?.nom.toLowerCase().localeCompare(b?.nom.toLowerCase()),
      )
      .map((student: any, index: number) => [
        `${index + 1} `,
        `${student.nom} ${student.postnom} ${capitalizeFirstLetter(student.prenom)}`,
        student.classeDesignation || "-",
        moment(selectedDate).format("DD/MM/YYYY"),
        student.coteEleveId === 1
          ? "Présent"
          : student.coteEleveId === 2
            ? "En retard"
            : "Absent",
      ]);

    autoTable(doc, {
      startY,
      head: [["N°", "Nom", "Classe", "Date de ponctualité", "Cotation"]],
      body: rows,
      theme: "striped",
      headStyles: {
        fillColor: [37, 99, 235],
        textColor: 255,
        fontStyle: "bold",
      },
      styles: { fontSize: 9, cellPadding: 3 },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      margin: { left: 14, right: 14 },
    });

    const pageCount = doc.internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Généré le ${moment().format("DD/MM/YYYY à HH:mm")} - ChikSoft`,
        14,
        pageHeight - 8,
      );
      doc.text(`Page ${i} / ${pageCount}`, pageWidth - 14, pageHeight - 8, {
        align: "right",
      });
    }

    doc.save(`liste_ponctualite_${moment().format("YYYY-MM-DD")}.pdf`);
  };

  const exportToExcel = () => {
    const rows = paginatedStudents.map((student: any, index: number) => ({
      "N°": index + 1,
      Nom: `${student.nom} ${student.postnom} ${capitalizeFirstLetter(student.prenom)}`,
      Classe: student.classeDesignation || "-",
      "Date de ponctualité": moment(selectedDate).format("DD/MM/YYYY"),
      Cotation:
        student.coteEleveId === 1
          ? "Présent"
          : student.coteEleveId === 2
            ? "En retard"
            : "Absent",
    }));

    const headerRows = [
      [SCHOOL_INFO.name],
      [SCHOOL_INFO.fullName],
      [`Tél: ${SCHOOL_INFO.telephone}`],
      [],
      [getExportTitle()],
      [],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(headerRows);
    XLSX.utils.sheet_add_json(worksheet, rows, { origin: headerRows.length });

    worksheet["!cols"] = [
      { wch: 32 },
      { wch: 18 },
      { wch: 12 },
      { wch: 16 },
      { wch: 18 },
    ];
    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: 4 } },
      { s: { r: 4, c: 0 }, e: { r: 4, c: 4 } },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Ponctualité");
    XLSX.writeFile(
      workbook,
      `liste_ponctualite_${moment().format("YYYY-MM-DD")}.xlsx`,
    );
  };

  const exportData = (format: "PDF" | "Excel") => {
    if (paginatedStudents.length === 0) {
      showExportNotification(
        "Aucune liste à exporter pour ce filtre.",
        "erreur",
      );
      setIsExportDropdownOpen(false);
      return;
    }

    try {
      if (format === "PDF") {
        exportToPDF();
      } else {
        exportToExcel();
      }
      showExportNotification(
        format === "PDF" ? "Export PDF réussi" : "Export Excel réussi",
        "success",
      );
    } catch (err) {
      showExportNotification("L'export a échoué, réessayez.", "erreur");
    }

    setIsExportDropdownOpen(false);
  };

  const currentCoteId = selectedStudent?.coteId ?? selectedTeacher?.coteId;

  const currentAttendanceCote =
    selectedStudent?.coteEleveId ?? selectedTeacher?.cotePersonalId;

  return (
    <div className="md:ml-60 md:w-[calc(100%-15rem)]">
      {" "}
      {/* Header */}
      <Header
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        toggleDarkMode={toggleDarkMode}
        currentYear={currentYear}
        isYearDropdownOpen={isYearDropdownOpen}
        toggleYearDropdown={toggleYearDropdown}
        setCurrentYear={setCurrentYear}
        setIsYearDropdownOpen={setIsYearDropdownOpen}
      />
      {/* Main Content */}
      {isAttendanceLoading ||
      isEleveLoading ||
      isPersonalAttendanceLoading ||
      isPromotionsLoading ||
      isClassesLoading ||
      isSchoolYearLoading ||
      isPersonalsLoading ? (
        <AttendanceSkeleton isDarkMode={isDarkMode} />
      ) : (
        <main className="ml-20 md:ml-0 p-6 mt-16">
          <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            {/* Titre */}
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  isDarkMode
                    ? "bg-blue-500/10 text-blue-400"
                    : "bg-blue-50 text-blue-600"
                }`}
              >
                <i className="fas fa-clipboard-check text-sm" />
              </div>

              <div>
                <h1 className="text-base font-semibold leading-tight">
                  Gestion des présences
                </h1>

                <p className="text-[11px] text-gray-500 mt-1">
                  Suivez la ponctualité des élèves et du personnel
                </p>
              </div>
            </div>

            {/* Navigation */}
            <div
              className={`inline-flex self-start xl:self-auto p-1 rounded-xl ${
                isDarkMode
                  ? "bg-[#161B22] border border-[#30363D]"
                  : "bg-gray-100"
              }`}
            >
              <button
                onClick={() => {
                  setActiveTab("students");
                  setCurrentPage(1);
                  setSearchQuery("");
                  setStatusFilter("tous");
                }}
                className={`h-9 px-4 rounded-lg flex items-center gap-2 text-xs
        font-medium transition-all ${
          activeTab === "students"
            ? isDarkMode
              ? "bg-[#30363D] text-white shadow-sm"
              : "bg-white text-blue-600 shadow-sm"
            : "text-gray-500 hover:text-gray-700"
        }`}
              >
                <i className="fas fa-user-graduate text-[11px]" />
                Élèves
                {activeTab === "students" && (
                  <span
                    className={`hidden sm:inline-flex px-1.5 py-0.5 rounded text-[9px] ${
                      isDarkMode
                        ? "bg-blue-500/10 text-blue-400"
                        : "bg-blue-50 text-blue-600"
                    }`}
                  >
                    {totalStudents}
                  </span>
                )}
              </button>

              <button
                onClick={() => {
                  setActiveTab("teachers");
                  setCurrentPage(1);
                  setSearchQuery("");
                  setStatusFilter("tous");
                }}
                className={`h-9 px-4 rounded-lg flex items-center gap-2 text-xs
        font-medium transition-all ${
          activeTab === "teachers"
            ? isDarkMode
              ? "bg-[#30363D] text-white shadow-sm"
              : "bg-white text-blue-600 shadow-sm"
            : "text-gray-500 hover:text-gray-700"
        }`}
              >
                <i className="fas fa-chalkboard-teacher text-[11px]" />
                Personnel
                {activeTab === "teachers" && (
                  <span
                    className={`hidden sm:inline-flex px-1.5 py-0.5 rounded text-[9px] ${
                      isDarkMode
                        ? "bg-blue-500/10 text-blue-400"
                        : "bg-blue-50 text-blue-600"
                    }`}
                  >
                    {totalTeachers}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Filters and Actions */}
          <div
            className={`mb-5 rounded-xl border p-4 ${
              isDarkMode
                ? "bg-[#0D1117] border-[#30363D]"
                : "bg-white border-gray-200 shadow-sm"
            }`}
          >
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Date */}
                <div>
                  <label className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                    <i className="far fa-calendar text-[9px]" />
                    Date
                  </label>

                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className={`h-10 px-3 rounded-lg border text-xs outline-none transition ${
                      isDarkMode
                        ? "bg-[#161B22] border-[#30363D] text-gray-200 focus:border-blue-500"
                        : "bg-gray-50 border-gray-200 focus:bg-white focus:border-blue-500"
                    }`}
                  />
                </div>

                {/* Classe */}
                {activeTab === "students" && (
                  <div>
                    <label className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                      <i className="fas fa-school text-[9px]" />
                      Classe
                    </label>

                    <div className="relative">
                      <select
                        value={selectedClass}
                        onChange={(e) => setSelectedClass(e.target.value)}
                        className={`appearance-none h-10 min-w-[190px] pl-3 pr-9
                rounded-lg border text-xs outline-none transition ${
                  isDarkMode
                    ? "bg-[#161B22] border-[#30363D] text-gray-200 focus:border-blue-500"
                    : "bg-gray-50 border-gray-200 focus:bg-white focus:border-blue-500"
                }`}
                      >
                        <option value="Toutes les classes">
                          Toutes les classes
                        </option>

                        {dataClasses
                          .sort((a, b) =>
                            a.designation.localeCompare(b.designation),
                          )
                          .map((classe) => (
                            <option key={classe.id} value={classe.designation}>
                              {classe.designation}
                            </option>
                          ))}
                      </select>

                      <i className="fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-[9px] text-gray-500 pointer-events-none" />
                    </div>
                  </div>
                )}
              </div>

              {/* Export */}
              <div className="relative" ref={ExportDropdownRef}>
                <button
                  onClick={toggleExportDropdown}
                  className={`h-10 px-4 rounded-lg border flex items-center gap-2
          text-xs font-medium transition ${
            isDarkMode
              ? "bg-[#161B22] border-[#30363D] hover:bg-[#21262D] text-gray-300"
              : "bg-white border-gray-200 hover:bg-gray-50 text-gray-600"
          }`}
                >
                  <i className="fas fa-download text-[10px]" />
                  Exporter
                  <i
                    className={`fas fa-chevron-down text-[8px] ml-1 transition-transform ${
                      isExportDropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {isExportDropdownOpen && (
                  <div
                    className={`absolute right-0 top-full mt-2 w-48 rounded-xl border
            shadow-xl overflow-hidden z-30 ${
              isDarkMode
                ? "bg-[#161B22] border-[#30363D]"
                : "bg-white border-gray-200"
            }`}
                  >
                    <div className="p-1.5">
                      <button
                        onClick={() => exportData("PDF")}
                        className={`w-full px-3 py-2.5 rounded-lg flex items-center
                gap-3 text-xs text-left transition ${
                  isDarkMode ? "hover:bg-[#21262D]" : "hover:bg-gray-50"
                }`}
                      >
                        <div className="w-7 h-7 rounded-md bg-red-500/10 text-red-500 flex items-center justify-center">
                          <i className="fas fa-file-pdf text-xs" />
                        </div>

                        <div>
                          <p className="font-medium">Format PDF</p>
                          <p className="text-[9px] text-gray-500">
                            Document imprimable
                          </p>
                        </div>
                      </button>

                      <button
                        onClick={() => exportData("Excel")}
                        className={`w-full px-3 py-2.5 rounded-lg flex items-center
                gap-3 text-xs text-left transition ${
                  isDarkMode ? "hover:bg-[#21262D]" : "hover:bg-gray-50"
                }`}
                      >
                        <div className="w-7 h-7 rounded-md bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
                          <i className="fas fa-file-excel text-xs" />
                        </div>

                        <div>
                          <p className="font-medium">Format Excel</p>
                          <p className="text-[9px] text-gray-500">
                            Feuille de calcul
                          </p>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Statistics */}
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 lg:gap-4 mb-5">
            {/* TOTAL */}
            <div
              className={`relative rounded-xl border p-4 overflow-hidden ${
                isDarkMode
                  ? "bg-[#0D1117] border-[#30363D]"
                  : "bg-white border-gray-200 shadow-sm"
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[11px] text-gray-500 font-medium">
                    {activeTab === "students"
                      ? "Total élèves"
                      : "Personnel actif"}
                  </p>

                  <p className="text-2xl lg:text-[28px] font-bold mt-2 leading-none">
                    {activeTab === "students" ? totalStudents : totalTeachers}
                  </p>
                </div>

                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                    isDarkMode
                      ? "bg-blue-500/10 text-blue-400"
                      : "bg-blue-50 text-blue-600"
                  }`}
                >
                  <i
                    className={`fas ${
                      activeTab === "students" ? "fa-users" : "fa-user-tie"
                    } text-xs`}
                  />
                </div>
              </div>

              <p className="text-[10px] text-gray-500 mt-5">
                {activeTab === "students"
                  ? selectedClass
                  : "Personnel enregistré"}
              </p>

              <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-blue-500" />
            </div>

            {/* TAUX PRÉSENCE */}
            <div
              className={`relative rounded-xl border p-4 overflow-hidden ${
                isDarkMode
                  ? "bg-[#0D1117] border-[#30363D]"
                  : "bg-white border-gray-200 shadow-sm"
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[11px] text-gray-500 font-medium">
                    Taux de présence
                  </p>

                  <p className="text-2xl lg:text-[28px] font-bold mt-2 leading-none">
                    {activeTab === "students"
                      ? attendanceRate
                      : teacherAttendanceRate}
                    %
                  </p>
                </div>

                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                    isDarkMode
                      ? "bg-emerald-500/10 text-emerald-400"
                      : "bg-emerald-50 text-emerald-600"
                  }`}
                >
                  <i className="fas fa-chart-line text-xs" />
                </div>
              </div>

              <div className="mt-5">
                <div
                  className={`h-1.5 rounded-full overflow-hidden ${
                    isDarkMode ? "bg-[#21262D]" : "bg-gray-100"
                  }`}
                >
                  <div
                    className="h-full bg-emerald-500 rounded-full"
                    style={{
                      width: `${
                        activeTab === "students"
                          ? attendanceRate
                          : teacherAttendanceRate
                      }%`,
                    }}
                  />
                </div>
              </div>

              <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-emerald-500" />
            </div>

            {/* ABSENTS */}
            <div
              className={`relative rounded-xl border p-4 overflow-hidden ${
                isDarkMode
                  ? "bg-[#0D1117] border-[#30363D]"
                  : "bg-white border-gray-200 shadow-sm"
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[11px] text-gray-500 font-medium">
                    Absents
                  </p>

                  <p className="text-2xl lg:text-[28px] font-bold mt-2 leading-none">
                    {activeTab === "students" ? absentStudents : absentTeachers}
                  </p>
                </div>

                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                    isDarkMode
                      ? "bg-red-500/10 text-red-400"
                      : "bg-red-50 text-red-500"
                  }`}
                >
                  <i className="fas fa-user-times text-xs" />
                </div>
              </div>

              <p className="text-[10px] text-gray-500 mt-5">
                Non présents à cette date
              </p>

              <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-red-500" />
            </div>

            {/* RETARDS */}
            <div
              className={`relative rounded-xl border p-4 overflow-hidden ${
                isDarkMode
                  ? "bg-[#0D1117] border-[#30363D]"
                  : "bg-white border-gray-200 shadow-sm"
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[11px] text-gray-500 font-medium">
                    En retard
                  </p>

                  <p className="text-2xl lg:text-[28px] font-bold mt-2 leading-none">
                    {activeTab === "students" ? lateStudents : lateTeachers}
                  </p>
                </div>

                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                    isDarkMode
                      ? "bg-amber-500/10 text-amber-400"
                      : "bg-amber-50 text-amber-500"
                  }`}
                >
                  <i className="fas fa-clock text-xs" />
                </div>
              </div>

              <p className="text-[10px] text-gray-500 mt-5">
                Arrivées après l'heure
              </p>

              <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-amber-500" />
            </div>
          </div>
          {/* Student List */}
          {activeTab === "students" && (
            <div
              className={`rounded-xl border overflow-hidden mb-12 ${
                isDarkMode
                  ? "bg-[#0D1117] border-[#30363D]"
                  : "bg-white border-gray-200 shadow-sm"
              }`}
            >
              <div
                className={`p-4 border-b ${
                  isDarkMode
                    ? "bg-[#161B22] border-[#30363D]"
                    : "bg-gray-50/70 border-gray-200"
                }`}
              >
                <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                  {/* Titre */}
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                        isDarkMode
                          ? "bg-blue-500/10 text-blue-400"
                          : "bg-blue-50 text-blue-600"
                      }`}
                    >
                      <i className="fas fa-user-graduate text-xs" />
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold">
                        Liste des élèves
                      </h3>

                      <p className="text-[10px] text-gray-500 mt-0.5">
                        {selectedClass} · {filteredStudents.length} résultat
                        {filteredStudents.length > 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row gap-3">
                    {/* Search */}
                    <div className="relative">
                      <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-[10px]" />

                      <input
                        type="text"
                        placeholder="Rechercher un élève..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={`h-9 w-full md:w-60 pl-9 pr-3 rounded-lg border
              text-xs outline-none transition ${
                isDarkMode
                  ? "bg-[#0D1117] border-[#30363D] focus:border-blue-500"
                  : "bg-white border-gray-200 focus:border-blue-500"
              }`}
                      />
                    </div>

                    {/* Status */}
                    <div
                      className={`flex items-center p-1 rounded-lg overflow-x-auto ${
                        isDarkMode ? "bg-[#0D1117]" : "bg-gray-100"
                      }`}
                    >
                      {[
                        {
                          label: "Tous",
                          value: "tous",
                          icon: "fa-layer-group",
                        },
                        {
                          label: "Présents",
                          value: 1,
                          icon: "fa-check",
                        },
                        {
                          label: "Absents",
                          value: 3,
                          icon: "fa-times",
                        },
                        {
                          label: "Retards",
                          value: 2,
                          icon: "fa-clock",
                        },
                      ].map((status) => (
                        <button
                          key={status.label}
                          onClick={() => setStatusFilter(status.value)}
                          className={`h-7 px-2.5 rounded-md flex items-center gap-1.5
                text-[10px] font-medium whitespace-nowrap transition ${
                  statusFilter === status.value
                    ? isDarkMode
                      ? "bg-[#30363D] text-white shadow-sm"
                      : "bg-white text-gray-800 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                        >
                          <i className={`fas ${status.icon} text-[8px]`} />
                          {status.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* tableau eleve */}
              <div className="w-full overflow-x-auto print-area" ref={printRef}>
                <table className="min-w-full divide-y divide-gray-700 mb-12">
                  <thead
                    className={isDarkMode ? "bg-[#161B22]" : "bg-gray-50/70"}
                  >
                    <tr>
                      <th className="px-5 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                        Elève
                      </th>

                      <th className="px-5 py-3 text-center text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                        Classe
                      </th>

                      <th className="px-5 py-3 text-center text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                        Statut
                      </th>
                      <th className="px-5 py-3 text-center text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                        Justification
                      </th>
                      <th className="px-5 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                        Heure d'arrivée
                      </th>
                      <th className="px-5 py-3 text-center text-[10px] font-semibold uppercase tracking-wider text-gray-500"></th>
                    </tr>
                  </thead>

                  <tbody
                    className={`${
                      isDarkMode ? "bg-[#0D1117]" : "bg-white"
                    } divide-y ${
                      isDarkMode ? "divide-gray-700" : "divide-gray-200"
                    }`}
                  >
                    {paginatedStudents && paginatedStudents.length > 0 ? (
                      paginatedStudents
                        .sort((a: any, b: any) =>
                          a.nom
                            .toLowerCase()
                            .localeCompare(b.nom.toLowerCase()),
                        )
                        .map((student: any, _: number) => {
                          return (
                            <tr
                              key={student.id}
                              className={`group transition-colors ${
                                isDarkMode
                                  ? "hover:bg-[#161B22]"
                                  : "hover:bg-blue-50/30"
                              }`}
                            >
                              <td className="px-5 py-3.5 whitespace-nowrap">
                                <div className="flex items-center gap-3">
                                  <div
                                    className={`w-9 h-9 rounded-lg flex items-center justify-center
                                            flex-shrink-0 text-[10px] font-semibold ${
                                              isDarkMode
                                                ? "bg-blue-500/10 text-blue-400"
                                                : "bg-blue-50 text-blue-600"
                                            }`}
                                  >
                                    {student.nom
                                      .split(" ")
                                      .map((n: any) => n[0])
                                      .join("")}
                                  </div>

                                  <div className="min-w-0">
                                    <p
                                      className={`text-xs font-semibold truncate ${
                                        isDarkMode
                                          ? "text-gray-200"
                                          : "text-gray-800"
                                      }`}
                                    >
                                      {`${student.nom} ${student.postnom} ${capitalizeFirstLetter(
                                        student.prenom,
                                      )}`}
                                    </p>

                                    <p className="text-[10px] text-gray-500 mt-0.5">
                                      Élève
                                    </p>
                                  </div>
                                </div>
                              </td>

                              <td className="px-5 py-3.5 whitespace-nowrap">
                                <span
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1
                                              rounded-md text-[10px] font-medium ${
                                                isDarkMode
                                                  ? "bg-blue-500/10 text-blue-400"
                                                  : "bg-blue-50 text-blue-600"
                                              }`}
                                >
                                  <i className="fas fa-graduation-cap text-[8px]" />
                                  {student.classeDesignation || "-"}
                                </span>
                              </td>

                              <td className="px-5 py-3.5 whitespace-nowrap text-center">
                                {student.coteEleveId == null && (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-500/10 text-gray-500 text-[10px] font-semibold">
                                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                                    Aucune activité
                                  </span>
                                )}

                                {student.coteEleveId === 1 && (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-500 text-[10px] font-semibold">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                    Présent
                                  </span>
                                )}

                                {student.coteEleveId === 2 && (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-500 text-[10px] font-semibold">
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                    En retard
                                  </span>
                                )}

                                {student.coteEleveId === 3 && (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/10 text-red-500 text-[10px] font-semibold">
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                                    Absent
                                  </span>
                                )}
                              </td>
                              <td className="px-5 py-3.5 text-center">
                                {student.coteJustification ? (
                                  <div
                                    className="inline-flex items-center gap-2 max-w-[190px]"
                                    title={student.coteJustification}
                                  >
                                    <i className="fas fa-file-alt text-blue-500 text-[10px]" />

                                    <span className="truncate text-[11px]">
                                      {student.coteJustification}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-[10px] text-gray-500">
                                    Non justifié
                                  </span>
                                )}
                              </td>
                              <td className="px-6 whitespace-nowrap text-center">
                                {student.coteEleveId === 2 ||
                                student.coteEleveId === 1 ? (
                                  <span
                                    className={`px-2 py-1 text-[10px] rounded-md border ${
                                      isDarkMode
                                        ? "bg-[#161B22] border-gray-700 text-white"
                                        : "bg-white border-gray-300"
                                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                  >
                                    {moment(student.coteCreatedAt).format(
                                      "HH:mm",
                                    )}
                                  </span>
                                ) : (
                                  <span>-</span>
                                )}
                              </td>
                              <td
                                className="px-6 whitespace-nowrap text-center
                                    opacity-0
                                    blur-sm
                                    transition-all duration-200
                                    group-hover:opacity-100
                                    group-hover:blur-0"
                              >
                                <div className="flex justify-center gap-2">
                                  {(student.coteEleveId === 3 ||
                                    student.coteEleveId === 2) && (
                                    <>
                                      <button
                                        onClick={() =>
                                          openJustifyModal(student)
                                        }
                                        className={`w-8 h-8 rounded-lg flex items-center justify-center
                                            transition ${
                                              isDarkMode
                                                ? "text-blue-400 hover:bg-blue-500/10"
                                                : "text-blue-600 hover:bg-blue-50"
                                            }`}
                                        title="Ajouter une justification"
                                      >
                                        <i className="fas fa-pen text-[10px]" />
                                      </button>
                                    </>
                                  )}
                                  {student.coteEleveId === 1 && (
                                    <button
                                      disabled
                                      className={`w-6 h-6 flex items-center justify-center rounded-full ${
                                        isDarkMode
                                          ? "hover:bg-gray-700"
                                          : "hover:bg-gray-200"
                                      } text-purple-500 !rounded-button whitespace-nowrap cursor-pointer`}
                                      title="Historique"
                                    >
                                      <i className="fa-solid fa-circle-check text-green-500 text-xs"></i>
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                    ) : (
                      <tr>
                        <td
                          colSpan={7}
                          className={`px-6 py-10 text-center text-sm font-medium ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          <span className="text-slate-500 text-sm lowercase">
                            Aucune cotation trouvée pour la date et la classe
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <div
                  className={`px-5 py-3.5 border-t flex flex-col sm:flex-row
                          sm:items-center sm:justify-between gap-3 ${
                            isDarkMode
                              ? "border-[#30363D] bg-[#0D1117]"
                              : "border-gray-100 bg-white"
                          }`}
                >
                  <p className="text-[10px] text-gray-500">
                    Affichage{" "}
                    <span className="font-semibold">{startIndex + 1}</span> à{" "}
                    <span className="font-semibold">
                      {Math.min(endIndex, filteredStudents.length)}
                    </span>{" "}
                    sur{" "}
                    <span className="font-semibold">
                      {filteredStudents.length}
                    </span>{" "}
                    élèves
                  </p>

                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() =>
                        setCurrentPage(Math.max(1, currentPage - 1))
                      }
                      disabled={currentPage === 1}
                      className={`w-8 h-8 rounded-lg border flex items-center
                            justify-center transition ${
                              isDarkMode
                                ? "border-[#30363D] hover:bg-[#161B22]"
                                : "border-gray-200 hover:bg-gray-50"
                            } ${currentPage === 1 ? "opacity-40 cursor-not-allowed" : ""}`}
                    >
                      <i className="fas fa-chevron-left text-[9px]" />
                    </button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;

                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`min-w-8 h-8 px-2 rounded-lg text-[10px]
              font-semibold transition ${
                currentPage === pageNum
                  ? "bg-blue-600 text-white"
                  : isDarkMode
                    ? "border border-[#30363D] hover:bg-[#161B22]"
                    : "border border-gray-200 hover:bg-gray-50"
              }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() =>
                        setCurrentPage(Math.min(totalPages, currentPage + 1))
                      }
                      disabled={currentPage === totalPages}
                      className={`w-8 h-8 rounded-lg border flex items-center
        justify-center transition ${
          isDarkMode
            ? "border-[#30363D] hover:bg-[#161B22]"
            : "border-gray-200 hover:bg-gray-50"
        } ${currentPage === totalPages ? "opacity-40 cursor-not-allowed" : ""}`}
                    >
                      <i className="fas fa-chevron-right text-[9px]" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Teacher List */}
          {activeTab === "teachers" && (
            <div
              className={`rounded-xl border overflow-hidden mb-12 ${
                isDarkMode
                  ? "bg-[#0D1117] border-[#30363D]"
                  : "bg-white border-gray-200 shadow-sm"
              }`}
            >
              {/* Header */}
              <div
                className={`p-4 border-b ${
                  isDarkMode
                    ? "bg-[#161B22] border-[#30363D]"
                    : "bg-gray-50/70 border-gray-200"
                }`}
              >
                <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                  {/* Titre */}
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                        isDarkMode
                          ? "bg-purple-500/10 text-purple-400"
                          : "bg-purple-50 text-purple-600"
                      }`}
                    >
                      <i className="fas fa-user-tie text-xs" />
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold">
                        Liste du personnel
                      </h3>

                      <p className="text-[10px] text-gray-500 mt-0.5">
                        {filteredTeachers.length} membre
                        {filteredTeachers.length > 1 ? "s" : ""} trouvé
                        {filteredTeachers.length > 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>

                  {/* Recherche + filtres */}
                  <div className="flex flex-col md:flex-row gap-3">
                    {/* Recherche */}
                    <div className="relative">
                      <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-[10px]" />

                      <input
                        type="text"
                        placeholder="Rechercher un membre du personnel..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={`h-9 w-full md:w-60 pl-9 pr-3 rounded-lg border
              text-xs outline-none transition ${
                isDarkMode
                  ? "bg-[#0D1117] border-[#30363D] focus:border-blue-500"
                  : "bg-white border-gray-200 focus:border-blue-500"
              }`}
                      />
                    </div>

                    {/* Filtres statut */}
                    <div
                      className={`flex items-center p-1 rounded-lg overflow-x-auto ${
                        isDarkMode ? "bg-[#0D1117]" : "bg-gray-100"
                      }`}
                    >
                      {[
                        {
                          label: "Tous",
                          value: "tous",
                          icon: "fa-layer-group",
                        },
                        {
                          label: "Présents",
                          value: 1,
                          icon: "fa-check",
                        },
                        {
                          label: "Absents",
                          value: 3,
                          icon: "fa-times",
                        },
                        {
                          label: "Retards",
                          value: 2,
                          icon: "fa-clock",
                        },
                      ].map((status) => (
                        <button
                          key={status.label}
                          onClick={() => setStatusFilter(status.value)}
                          className={`h-7 px-2.5 rounded-md flex items-center gap-1.5
                text-[10px] font-medium whitespace-nowrap transition ${
                  statusFilter === status.value
                    ? isDarkMode
                      ? "bg-[#30363D] text-white shadow-sm"
                      : "bg-white text-gray-800 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                        >
                          <i className={`fas ${status.icon} text-[8px]`} />
                          {status.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* tableau personnel */}
              <div className="w-full overflow-x-auto">
                <table className="w-full min-w-[760px]">
                  <thead
                    className={isDarkMode ? "bg-[#161B22]" : "bg-gray-50/70"}
                  >
                    <tr>
                      <th className="px-5 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                        Personnel
                      </th>

                      <th className="px-5 py-3 text-center text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                        Statut
                      </th>

                      <th className="px-5 py-3 text-center text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                        Justification
                      </th>
                      <th className="px-5 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                        Heure d'arrivée
                      </th>
                      <th className="px-5 py-3 text-center text-[10px] font-semibold uppercase tracking-wider text-gray-500"></th>
                    </tr>
                  </thead>

                  <tbody
                    className={`divide-y ${
                      isDarkMode ? "divide-[#21262D]" : "divide-gray-100"
                    }`}
                  >
                    {paginatedTeachers && paginatedTeachers.length > 0 ? (
                      paginatedTeachers.map(
                        (
                          teacher: PersonalType & {
                            cotePersonalId: number;
                            coteJustification: string;
                            coteId: number;
                            coteCreatedAt: string;
                          },
                        ) => (
                          <tr
                            key={teacher.id}
                            className={`group transition-colors ${
                              isDarkMode
                                ? "hover:bg-[#161B22]"
                                : "hover:bg-blue-50/30"
                            }`}
                          >
                            <td className="px-5 py-3.5 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                <div
                                  className={`w-9 h-9 rounded-lg flex items-center justify-center
                                    flex-shrink-0 text-[10px] font-semibold ${
                                      isDarkMode
                                        ? "bg-purple-500/10 text-purple-400"
                                        : "bg-purple-50 text-purple-600"
                                    }`}
                                >
                                  {teacher.nom
                                    ?.split(" ")
                                    .map((n: any) => n[0])
                                    .join("")}
                                </div>

                                <div className="min-w-0">
                                  <p
                                    className={`text-xs font-semibold truncate ${
                                      isDarkMode
                                        ? "text-gray-200"
                                        : "text-gray-800"
                                    }`}
                                  >
                                    {`${teacher.nom} ${teacher.postnom || ""} ${teacher.prenom || ""}`}
                                  </p>

                                  <p className="text-[10px] text-gray-500 mt-0.5">
                                    Personnel
                                  </p>
                                </div>
                              </div>
                            </td>

                            <td className="px-5 py-3.5 whitespace-nowrap text-center">
                              {teacher.coteId == null ? (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-500/10 text-gray-500 text-[10px] font-semibold">
                                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                                  Aucune activité
                                </span>
                              ) : teacher.cotePersonalId === 1 ? (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-500 text-[10px] font-semibold">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                  Présent
                                </span>
                              ) : teacher.cotePersonalId === 2 ? (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-500 text-[10px] font-semibold">
                                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                  En retard
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/10 text-red-500 text-[10px] font-semibold">
                                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                                  Absent
                                </span>
                              )}
                            </td>
                            <td className="px-6 whitespace-nowrap text-center">
                              <div className="flex justify-center space-x-2">
                                {teacher.coteJustification || <span>-</span>}
                              </div>
                            </td>
                            <td className="px-6 whitespace-nowrap text-center">
                              {teacher.cotePersonalId === 2 ||
                              teacher.cotePersonalId === 1 ? (
                                <span
                                  className={`px-2 py-1 rounded-md border ${
                                    isDarkMode
                                      ? "bg-[#161B22] border-gray-700 text-white"
                                      : "bg-white border-gray-300"
                                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                >
                                  {moment(teacher.coteCreatedAt).format(
                                    "HH:mm",
                                  )}
                                </span>
                              ) : (
                                <span>-</span>
                              )}
                            </td>
                            <td
                              className="px-6 whitespace-nowrap text-center
                                      opacity-0
                                      blur-sm
                                      transition-all duration-200
                                      group-hover:opacity-100
                                      group-hover:blur-0"
                            >
                              <div className="flex justify-center gap-2">
                                {(teacher.cotePersonalId === 3 ||
                                  teacher.cotePersonalId === 2) && (
                                  <button
                                    onClick={() =>
                                      openJustifyTeacherModal(teacher)
                                    }
                                    className={`w-8 h-8 rounded-lg flex items-center justify-center
                                              transition ${
                                                isDarkMode
                                                  ? "text-blue-400 hover:bg-blue-500/10"
                                                  : "text-blue-600 hover:bg-blue-50"
                                              }`}
                                    title="Ajouter une justification"
                                  >
                                    <i className="fas fa-file-signature text-[10px]" />
                                  </button>
                                )}
                                {teacher.cotePersonalId === 1 && (
                                  <div
                                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                      isDarkMode
                                        ? "bg-emerald-500/10"
                                        : "bg-emerald-50"
                                    }`}
                                    title="Présence confirmée"
                                  >
                                    <i className="fas fa-circle-check text-emerald-500 text-xs" />
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        ),
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan={7}
                          className={`px-6 py-10 text-center text-sm font-medium ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          <span className="text-slate-500 text-sm lowercase">
                            Aucune cotation trouvée pour la date du{" "}
                            {moment().format("DD/MM/YYYY")}
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Bottom Action Bar */}
          <div
            className={`fixed bottom-0 left-20 md:left-60 right-0 py-3 px-6 border-t bg-opacity-70 backdrop-blur-sm ${
              isDarkMode
                ? "bg-[#0D1117] border-gray-700"
                : "bg-white border-gray-200"
            } flex justify-between items-center`}
          >
            <div>
              <span className=" text-gray-600 font-mono text-xs">
                Date du jour: {moment().format("DD/MM/YYYY")}
              </span>
            </div>
            <div className="flex space-x-3 text-xs text-gray-600 font-mono">
              ChikSoft v2.0.1
            </div>
          </div>
        </main>
      )}
      {/* Justify Modal */}
      {isJustifyModalOpen && (selectedStudent || selectedTeacher) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
            onClick={() => {
              setIsJustifyModalOpen(false);
              setSelectedStudent(null);
              setSelectedTeacher(null);
            }}
          />

          {/* Modal */}
          <div
            className={`relative w-full max-w-md rounded-2xl border shadow-2xl overflow-hidden ${
              isDarkMode
                ? "bg-[#0D1117] border-[#30363D]"
                : "bg-white border-gray-200"
            }`}
          >
            {/* Header */}
            <div
              className={`px-5 py-4 border-b flex items-center justify-between ${
                isDarkMode
                  ? "bg-[#161B22] border-[#30363D]"
                  : "bg-gray-50 border-gray-200"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center">
                  <i className="fas fa-file-signature text-xs" />
                </div>

                <div>
                  <h3 className="text-sm font-semibold">
                    Justifier la ponctualité
                  </h3>

                  <p className="text-[10px] text-gray-500 mt-0.5">
                    Ajouter le motif de l'absence ou du retard
                  </p>
                </div>
              </div>

              <button
                onClick={() => {
                  setIsJustifyModalOpen(false);
                  setSelectedStudent(null);
                  setSelectedTeacher(null);
                }}
                className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  isDarkMode ? "hover:bg-[#21262D]" : "hover:bg-gray-100"
                }`}
              >
                <i className="fas fa-times text-xs text-gray-500" />
              </button>
            </div>

            <div className="p-5">
              {/* Personne */}
              <div
                className={`flex items-center gap-3 p-3 rounded-xl mb-5 ${
                  isDarkMode ? "bg-[#161B22]" : "bg-gray-50"
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center
              text-xs font-semibold ${
                isDarkMode
                  ? "bg-blue-500/10 text-blue-400"
                  : "bg-blue-50 text-blue-600"
              }`}
                >
                  {(selectedStudent?.nom || selectedTeacher?.nom)
                    ?.split(" ")
                    .map((n: any) => n[0])
                    .join("")}
                </div>

                <div className="min-w-0">
                  <p className="text-xs font-semibold truncate">
                    {selectedStudent
                      ? `${selectedStudent.nom} ${selectedStudent.postnom || ""}`
                      : `${selectedTeacher?.nom} ${selectedTeacher?.postnom || ""}`}
                  </p>

                  <p className="text-[10px] text-gray-500 mt-1">
                    {selectedStudent
                      ? `${selectedStudent.classeDesignation} · ${moment(
                          selectedDate,
                        ).format("DD/MM/YYYY")}`
                      : moment(selectedDate).format("DD/MM/YYYY")}
                  </p>
                </div>
              </div>

              {/* Statut de la ponctualité */}
              {/* État de la ponctualité */}
              <div
                className={`mb-5 px-3.5 py-3 rounded-xl border flex items-start gap-3 ${
                  currentCoteId == null
                    ? isDarkMode
                      ? "bg-gray-500/10 border-gray-500/20"
                      : "bg-gray-50 border-gray-200"
                    : currentAttendanceCote === 3
                      ? isDarkMode
                        ? "bg-red-500/10 border-red-500/20"
                        : "bg-red-50 border-red-100"
                      : isDarkMode
                        ? "bg-amber-500/10 border-amber-500/20"
                        : "bg-amber-50 border-amber-100"
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    currentCoteId == null
                      ? "bg-gray-500/10 text-gray-500"
                      : currentAttendanceCote === 3
                        ? "bg-red-500/10 text-red-500"
                        : "bg-amber-500/10 text-amber-500"
                  }`}
                >
                  <i
                    className={`fas ${
                      currentCoteId == null
                        ? "fa-calendar-times"
                        : currentAttendanceCote === 3
                          ? "fa-user-times"
                          : "fa-clock"
                    } text-xs`}
                  />
                </div>

                <div>
                  <p
                    className={`text-xs font-semibold ${
                      currentCoteId == null
                        ? "text-gray-500"
                        : currentAttendanceCote === 3
                          ? "text-red-500"
                          : "text-amber-500"
                    }`}
                  >
                    {currentCoteId == null
                      ? "Aucune activité"
                      : currentAttendanceCote === 3
                        ? "Absent"
                        : "En retard"}
                  </p>

                  <p className="text-[10px] text-gray-500 mt-1 leading-relaxed">
                    {currentCoteId == null
                      ? "Aucune activité n’a été enregistrée pour cette journée. Aucune justification n’est donc nécessaire."
                      : currentAttendanceCote === 3
                        ? "Cette personne a été enregistrée comme absente pour cette journée."
                        : "Cette personne a été enregistrée en retard pour cette journée."}
                  </p>
                </div>
              </div>

              {/* Motif */}
              <div className="mb-5">
                <label className="block text-[11px] font-semibold mb-2">
                  Motif de justification
                </label>
                <textarea
                  value={justificationForm.reason || ""}
                  onChange={(e) =>
                    setJustificationForm((prev) => ({
                      ...prev,
                      reason: e.target.value,
                    }))
                  }
                  disabled={currentCoteId == null}
                  rows={4}
                  placeholder={
                    currentCoteId == null
                      ? "Aucune justification nécessaire"
                      : "Saisissez le motif de l'absence ou du retard..."
                  }
                  className={`w-full px-3.5 py-3 rounded-xl border text-xs
    resize-none outline-none transition ${
      isDarkMode
        ? "bg-[#161B22] border-[#30363D] focus:border-blue-500"
        : "bg-white border-gray-200 focus:border-blue-500"
    } ${currentCoteId == null ? "opacity-50 cursor-not-allowed" : ""}`}
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  disabled={
                    mutationUpdateAttendanceStudent.isPending ||
                    mutationUpadateAttenadaceTeacher.isPending
                  }
                  onClick={() => {
                    setIsJustifyModalOpen(false);
                    setSelectedStudent(null);
                    setSelectedTeacher(null);
                  }}
                  className={`h-9 px-4 rounded-lg border text-xs font-medium ${
                    isDarkMode
                      ? "border-[#30363D] hover:bg-[#161B22]"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  Annuler
                </button>

                <button
                  disabled={
                    mutationUpdateAttendanceStudent.isPending ||
                    mutationUpadateAttenadaceTeacher.isPending ||
                    currentCoteId == null
                  }
                  onClick={handleUpdateAttendance}
                  className={`h-9 px-4 rounded-lg text-xs font-medium text-white
    flex items-center gap-2 transition ${
      mutationUpdateAttendanceStudent.isPending ||
      mutationUpadateAttenadaceTeacher.isPending ||
      currentCoteId == null
        ? "bg-blue-400 cursor-not-allowed"
        : "bg-blue-600 hover:bg-blue-700 cursor-pointer"
    }`}
                >
                  {(mutationUpdateAttendanceStudent.isPending ||
                    mutationUpadateAttenadaceTeacher.isPending) && (
                    <i className="fas fa-circle-notch fa-spin text-[10px]" />
                  )}

                  {mutationUpdateAttendanceStudent.isPending ||
                  mutationUpadateAttenadaceTeacher.isPending
                    ? "Enregistrement..."
                    : "Enregistrer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* {isJustifyModalOpen && (selectedStudent || selectedTeacher) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => {
              setIsJustifyModalOpen(false);
              setSelectedStudent(null);
              setSelectedTeacher(null);
            }}
          ></div>
          <div
            className={`relative w-full max-w-md p-6 rounded-lg shadow-xl ${
              isDarkMode ? "bg-[#161B22]" : "bg-white"
            }`}
          >
            <h3 className="text-xl font-semibold mb-4">
              Justifier{" "}
              {(selectedStudent?.coteEleveId ||
                selectedTeacher?.cotePersonnelId) === 2
                ? "le retard"
                : "l'absence"}
            </h3>
            <div>
              <div className="mb-4">
                <div className="flex items-center mb-3">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isDarkMode ? "bg-gray-700" : "bg-gray-200"
                    } text-gray-500 mr-3`}
                  >
                    <span>
                      {(selectedStudent?.nom || selectedTeacher?.nom)
                        ?.split(" ")
                        .map((n: any) => n[0])
                        .join("")}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">
                      {selectedStudent?.nom ||
                        `${selectedTeacher?.nom} ${selectedTeacher?.postnom}`}
                    </p>
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {selectedStudent
                        ? `${selectedStudent.classeDesignation} • ${moment(selectedDate).format("DD/MM/YYYY")}`
                        : ` `}
                    </p>
                  </div>
                </div>
                <div
                  className={`p-3 rounded-lg mb-4 ${
                    (selectedStudent?.status || selectedTeacher?.status) ===
                    "absent"
                      ? isDarkMode
                        ? "bg-red-900 bg-opacity-20"
                        : "bg-red-50"
                      : isDarkMode
                        ? "bg-yellow-900 bg-opacity-20"
                        : "bg-yellow-50"
                  } border ${
                    (selectedStudent?.status || selectedTeacher?.status) ===
                    "absent"
                      ? isDarkMode
                        ? "border-red-800"
                        : "border-red-100"
                      : isDarkMode
                        ? "border-yellow-800"
                        : "border-yellow-100"
                  }`}
                >
                  <div className="flex items-center">
                    <i
                      className={`fas ${
                        (selectedStudent?.status || selectedTeacher?.status) ===
                        "absent"
                          ? "fa-times-circle text-red-500"
                          : "fa-clock text-yellow-500"
                      } mr-2`}
                    ></i>
                    <p
                      className={`${
                        (selectedStudent?.coteEleveId ||
                          selectedTeacher?.cotePersonnelId) === 3
                          ? isDarkMode
                            ? "text-red-300"
                            : "text-red-800"
                          : isDarkMode
                            ? "text-yellow-300"
                            : "text-yellow-800"
                      }`}
                    >
                      {(selectedStudent?.coteId || selectedTeacher?.coteId) ==
                      null
                        ? "Aucune activité"
                        : (selectedStudent?.coteEleveId ||
                              selectedTeacher?.cotePersonnelId) === 3
                          ? "Absent "
                          : "En retard "}
                      le {selectedDate.split("-").reverse().join("/")}
                      {(selectedStudent?.coteEleveId ||
                        selectedTeacher?.cotePersonnelId) === 2 &&
                      (selectedStudent?.created_at ||
                        selectedTeacher?.created_at)
                        ? ` (Arrivée à ${
                            moment(selectedStudent?.created_at).format(
                              "HH:mm",
                            ) ||
                            moment(selectedTeacher?.created_at).format("HH:mm")
                          })`
                        : ""}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mb-4">
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Motif de justification
                </label>
                <select
                  value={justificationForm.reason}
                  onChange={(e) =>
                    setJustificationForm((prev) => ({
                      ...prev,
                      reason: e.target.value,
                    }))
                  }
                  className={`w-full px-3 py-1 rounded-md border ${
                    isDarkMode
                      ? "bg-[#0D1117] border-gray-700 text-white"
                      : "bg-white border-gray-300"
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  required
                >
                  <option value="">Sélectionner un motif</option>
                  <option value="Maladie">Maladie</option>
                  <option value="Rendez-vous médical">
                    Rendez-vous médical
                  </option>
                  <option value="Problème familial">Problème familial</option>
                  <option value="Transport">Problème de transport</option>
                  {selectedTeacher && (
                    <option value="Formation">Formation</option>
                  )}
                  {selectedTeacher && <option value="Réunion">Réunion</option>}
                  <option value="Autre">Autre</option>
                </select>
              </div>
          

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  disabled={
                    mutationUpdateAttendanceStudent.isPending ||
                    mutationUpadateAttenadaceTeacher.isPending
                  }
                  onClick={() => {
                    setIsJustifyModalOpen(false);
                    setSelectedStudent(null);
                    setSelectedTeacher(null);
                  }}
                  className={`px-4 py-2 rounded-md ${
                    (mutationUpdateAttendanceStudent.isPending ||
                      mutationUpadateAttenadaceTeacher.isPending) &&
                    "cursor-not-allowed"
                  } ${
                    isDarkMode
                      ? "bg-gray-700 hover:bg-gray-600"
                      : "bg-gray-200 hover:bg-gray-300"
                  } !rounded-button whitespace-nowrap`}
                >
                  Annuler
                </button>
                <button
                  disabled={
                    mutationUpdateAttendanceStudent.isPending ||
                    mutationUpadateAttenadaceTeacher.isPending ||
                    selectedStudent?.coteId === null ||
                    selectedTeacher?.coteId === null
                  }
                  onClick={() => {
                    handleUpdateAttendance();
                  }}
                  className={`px-4 py-2 ${
                    mutationUpdateAttendanceStudent.isPending ||
                    mutationUpadateAttenadaceTeacher.isPending ||
                    selectedStudent?.coteId === null ||
                    selectedTeacher?.coteId === null
                      ? "cursor-not-allowed  bg-blue-400"
                      : "cursor-pointer  bg-blue-500 hover:bg-blue-700"
                  } rounded-md text-white !rounded-button whitespace-nowrap`}
                >
                  {mutationUpdateAttendanceStudent.isPending ||
                  mutationUpadateAttenadaceTeacher.isPending
                    ? "Chargement..."
                    : "Mettre à jour"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )} */}
      {/* Notify Modal */}
      {isNotifyModalOpen && selectedStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setIsNotifyModalOpen(false)}
          ></div>
          <div
            className={`relative w-full max-w-md p-6 rounded-lg shadow-xl ${
              isDarkMode ? "bg-[#161B22]" : "bg-white"
            }`}
          >
            <h3 className="text-xl font-semibold mb-4">Notifier les parents</h3>
            <form onSubmit={handleNotifySubmit}>
              <div className="mb-4">
                <div className="flex items-center mb-3">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isDarkMode ? "bg-gray-700" : "bg-gray-200"
                    } text-gray-500 mr-3`}
                  >
                    <span>
                      {selectedStudent.nom
                        .split(" ")
                        .map((n: any) => n[0])
                        .join("")}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">Elève {selectedStudent.nom}</p>
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {selectedStudent.classe} • {selectedDate}
                    </p>
                  </div>
                </div>
                <div
                  className={`p-3 rounded-lg mb-4 ${
                    selectedStudent.coteEleveId === 3
                      ? isDarkMode
                        ? "bg-red-900 bg-opacity-20"
                        : "bg-red-50"
                      : isDarkMode
                        ? "bg-yellow-900 bg-opacity-20"
                        : "bg-yellow-50"
                  } border ${
                    selectedStudent.coteEleveId === 3
                      ? isDarkMode
                        ? "border-red-800"
                        : "border-red-100"
                      : isDarkMode
                        ? "border-yellow-800"
                        : "border-yellow-100"
                  }`}
                >
                  <div className="flex items-center">
                    <i
                      className={`fas ${
                        selectedStudent.coteEleveId === 3
                          ? "fa-times-circle text-red-500"
                          : "fa-clock text-yellow-500"
                      } mr-2`}
                    ></i>
                    <p
                      className={`${
                        selectedStudent.coteEleveId === 3
                          ? isDarkMode
                            ? "text-red-300"
                            : "text-red-800"
                          : isDarkMode
                            ? "text-yellow-300"
                            : "text-yellow-800"
                      }`}
                    >
                      {selectedStudent.coteEleveId === 3
                        ? "Absent"
                        : "En retard"}{" "}
                      le {selectedDate.split("-").reverse().join("/")}
                      {selectedStudent.coteEleveId === 2 &&
                      selectedStudent.arrivalTime
                        ? ` (Arrivée à ${selectedStudent.arrivalTime})`
                        : ""}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Sujet
                </label>
                <input
                  type="text"
                  value={`Notification ${
                    selectedStudent.coteEleveId === 3
                      ? "d'absence"
                      : "de retard"
                  } - ${selectedStudent.nom}`}
                  readOnly
                  className={`w-full px-3 py-2 rounded-md border ${
                    isDarkMode
                      ? "bg-[#0D1117] border-gray-700 text-white"
                      : "bg-white border-gray-300"
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>
              <div className="mb-4">
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Message
                </label>
                <textarea
                  rows={5}
                  defaultValue={`Madame, Monsieur,\n\nNous vous informons que votre enfant ${
                    selectedStudent.nom
                  } a été ${
                    selectedStudent.coteEleveId === 3 ? "absent" : "en retard"
                  } le ${selectedDate
                    .split("-")
                    .reverse()
                    .join(
                      "/",
                    )}.\n\nNous vous prions de bien vouloir nous fournir un justificatif dans les plus brefs délais.\n\nCordialement,\nL'administration`}
                  className={`w-full px-3 py-2 rounded-md border ${
                    isDarkMode
                      ? "bg-[#0D1117] border-gray-700 text-white"
                      : "bg-white border-gray-300"
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                ></textarea>
              </div>
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span
                    className={`ml-2 text-sm ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Envoyer également par SMS
                  </span>
                </label>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsNotifyModalOpen(false)}
                  className={`px-4 py-2 rounded-md ${
                    isDarkMode
                      ? "bg-gray-700 hover:bg-gray-600"
                      : "bg-gray-200 hover:bg-gray-300"
                  } !rounded-button whitespace-nowrap`}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white !rounded-button whitespace-nowrap`}
                >
                  Envoyer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Notification */}
      {showNotification && (
        <div className="fixed bottom-4 right-4 z-50">
          <div
            className={`px-4 py-3 rounded-lg shadow-lg ${
              isDarkMode ? "bg-green-900" : "bg-green-100"
            } border ${isDarkMode ? "border-green-800" : "border-green-200"}`}
          >
            <div className="flex items-center">
              <i className="fas fa-check-circle text-green-500 mr-2"></i>
              <p
                className={`${
                  isDarkMode ? "text-green-100" : "text-green-800"
                }`}
              >
                {notificationMessage}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attendance;
